import React, { useState, useEffect } from 'react'
import { SplineScene } from './ui/spline'
import { Card } from './ui/card'
import { Button } from './ui/button'
import { motion } from 'framer-motion'
import { FaPlay, FaArrowRight, FaRobot, FaIndustry, FaHome, FaCube, FaUsers, FaCoins } from 'react-icons/fa'
import { generateWhitepaperPDF } from '../utils/generateWhitepaper'
import { useWallet } from '../contexts/WalletContext'

export function HeroSection() {
  const { account } = useWallet()
  const [stats, setStats] = useState({
    activeRobots: 0,
    tokenHolders: 0,
    servicesCompleted: 0
  })

  // Animated counter effect
  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        activeRobots: Math.min(prev.activeRobots + Math.floor(Math.random() * 3), 2847),
        tokenHolders: Math.min(prev.tokenHolders + Math.floor(Math.random() * 5), 67429),
        servicesCompleted: Math.min(prev.servicesCompleted + Math.floor(Math.random() * 10), 1247382)
      }))
    }, 150)

    setTimeout(() => clearInterval(interval), 3000)
    return () => clearInterval(interval)
  }, [])

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleDownloadWhitepaper = () => {
    try {
      console.log('Generating whitepaper PDF...')
      generateWhitepaperPDF(account)
      console.log('Whitepaper generated successfully')
    } catch (error) {
      console.error('Error generating whitepaper:', error)
      alert('Failed to generate whitepaper. Please try again.')
    }
  }

  return (
    <div className="relative min-h-screen bg-deep-charcoal overflow-hidden">
      {/* Deep Tech Noir Background */}
      <div className="absolute inset-0 bg-deep-tech-gradient"></div>
      
      {/* Subtle Tech Grid Pattern */}
      <div className="absolute inset-0 opacity-20 tech-grid"></div>

      {/* Scanning Line Effect */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-transparent via-electric-blue to-transparent opacity-60 animate-scan-line"></div>

      {/* Enhanced Background Image with Noir Overlay */}
      <div 
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1655393001768-d946c97d6fd1)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      <div className="absolute inset-0 bg-noir-overlay"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-screen py-12">
          {/* Left Content - Following Design Doc Specifications */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="space-y-10"
          >
            {/* Primary Headline */}
            <div className="space-y-6">
              <h1 className="font-heading text-hero font-black leading-tight tracking-tight text-bright-white">
                <span className="block animate-tech-glow">Intelligence</span>
                <span className="block text-transparent bg-clip-text bg-electric-gradient animate-fade-in-up">
                  in Motion.
                </span>
                <span className="block text-electric-blue text-display animate-fade-in-up">
                  Decentralized.
                </span>
              </h1>
              
              {/* Subheading */}
              <p className="font-body text-xl md:text-2xl text-subtle-gray max-w-2xl leading-relaxed">
                Access <span className="text-electric-blue font-semibold">2,847+ industrial robots</span> and 
                <span className="text-tech-cyan font-semibold"> personal companions</span> using 
                <span className="text-electric-blue font-bold"> $BODY tokens</span>. 
                Live in <span className="text-bright-white font-semibold">23 countries</span> across 6 continents.
              </p>
            </div>

            {/* Live Stats Counter */}
            <div className="grid grid-cols-3 gap-8 py-8">
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-black text-electric-blue mb-2 animate-tech-glow">
                  {stats.activeRobots.toLocaleString()}+
                </div>
                <div className="text-sm text-subtle-gray uppercase tracking-wider">Active Robots</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-black text-tech-cyan mb-2 animate-tech-glow">
                  {stats.tokenHolders.toLocaleString()}+
                </div>
                <div className="text-sm text-subtle-gray uppercase tracking-wider">Token Holders</div>
              </div>
              <div className="text-center">
                <div className="text-4xl md:text-5xl font-black text-accent-electric mb-2 animate-tech-glow">
                  {stats.servicesCompleted.toLocaleString()}+
                </div>
                <div className="text-sm text-subtle-gray uppercase tracking-wider">Services Completed</div>
              </div>
            </div>

            {/* Core Pillars Preview - Interactive */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 py-8">
              <motion.div 
                className="text-center p-6 bg-deep-navy/50 rounded-xl border border-electric-blue/20 backdrop-blur-sm hover:border-electric-blue/40 transition-all duration-300 group cursor-pointer"
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={() => scrollToSection('technology')}
              >
                <FaCube className="text-4xl text-electric-blue mx-auto mb-4 group-hover:scale-110 transition-transform animate-pulse-electric" />
                <h3 className="font-heading text-lg font-bold text-bright-white mb-2">Decentralized AI</h3>
                <p className="text-subtle-gray text-sm">Advanced ML models powering autonomous decision-making</p>
                <div className="mt-4 text-xs text-electric-blue font-bold">2,847 ACTIVE NODES</div>
              </motion.div>
              <motion.div 
                className="text-center p-6 bg-deep-navy/50 rounded-xl border border-electric-blue/20 backdrop-blur-sm hover:border-electric-blue/40 transition-all duration-300 group cursor-pointer"
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={() => scrollToSection('technology')}
              >
                <FaRobot className="text-4xl text-tech-cyan mx-auto mb-4 group-hover:scale-110 transition-transform animate-pulse-electric" />
                <h3 className="font-heading text-lg font-bold text-bright-white mb-2">Autonomous Robotics</h3>
                <p className="text-subtle-gray text-sm">Physical robots bridging digital-to-real world gap</p>
                <div className="mt-4 text-xs text-tech-cyan font-bold">99.7% UPTIME</div>
              </motion.div>
              <motion.div 
                className="text-center p-6 bg-deep-navy/50 rounded-xl border border-electric-blue/20 backdrop-blur-sm hover:border-electric-blue/40 transition-all duration-300 group cursor-pointer"
                whileHover={{ scale: 1.05, y: -5 }}
                onClick={() => scrollToSection('ecosystem')}
              >
                <FaCoins className="text-4xl text-accent-electric mx-auto mb-4 group-hover:scale-110 transition-transform animate-pulse-electric" />
                <h3 className="font-heading text-lg font-bold text-bright-white mb-2">Crypto Economy</h3>
                <p className="text-subtle-gray text-sm">$BODY tokens enabling accessible robot services</p>
                <div className="mt-4 text-xs text-accent-electric font-bold">PRE-LAUNCH • PRESALE JAN 27-30</div>
              </motion.div>
            </div>

            {/* Enhanced CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-6 pt-6">
              <Button 
                size="lg" 
                className="group relative overflow-hidden bg-electric-gradient hover:shadow-electric transition-all duration-300 font-heading font-bold text-lg px-10 py-5"
                onClick={() => scrollToSection('technology')}
              >
                <span className="flex items-center relative z-10">
                  Explore Live Robots
                  <FaArrowRight className="ml-3 group-hover:translate-x-2 transition-transform duration-300" />
                </span>
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                className="border-2 border-electric-blue/70 text-electric-blue hover:bg-electric-blue/10 backdrop-blur-md hover:shadow-interactive transition-all duration-300 font-heading font-bold text-lg px-10 py-5"
                onClick={handleDownloadWhitepaper}
              >
                <FaPlay className="mr-3" />
                Read Whitepaper 2026
              </Button>
            </div>

            {/* Enhanced Trust Indicators */}
            <div className="pt-8 border-t border-subtle-gray/20">
              <p className="text-subtle-gray text-sm font-body mb-4 uppercase tracking-wider">Featured In</p>
              <div className="flex items-center space-x-8 opacity-60">
                <div className="text-xs text-subtle-gray font-semibold">TechCrunch 2025</div>
                <div className="text-xs text-subtle-gray font-semibold">Forbes AI 100</div>
                <div className="text-xs text-subtle-gray font-semibold">MIT Tech Review</div>
                <div className="text-xs text-subtle-gray font-semibold">Wired Future</div>
              </div>
            </div>
          </motion.div>

          {/* Right Content - Enhanced Interactive 3D Scene */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative z-10">
              <Card className="p-8 bg-deep-navy/60 backdrop-blur-xl border-2 border-electric-blue/30 shadow-deep-tech hover:shadow-electric transition-all duration-500">
                {/* 3D Robot Model Container */}
                <div className="relative">
                  <SplineScene 
                    scene="https://prod.spline.design/kZDDjO5HuC9GJUM2/scene.splinecode"
                    className="w-full h-96"
                  />
                  
                  {/* Live Status Indicators */}
                  <div className="absolute top-4 right-4 space-y-2">
                    <div className="bg-electric-blue/20 backdrop-blur-sm rounded-full px-3 py-1 border border-electric-blue/40 flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-xs text-electric-blue font-bold">LIVE</span>
                    </div>
                    <div className="bg-tech-cyan/20 backdrop-blur-sm rounded-full px-3 py-1 border border-tech-cyan/40">
                      <span className="text-xs text-tech-cyan font-bold">Tokyo Factory #247</span>
                    </div>
                  </div>
                </div>
                
                {/* Enhanced Technology Description */}
                <div className="mt-6 pt-6 border-t border-electric-blue/20">
                  <h3 className="font-heading text-2xl font-bold text-bright-white mb-3">
                    Real-Time Robot Network
                  </h3>
                  <p className="font-body text-subtle-gray leading-relaxed mb-4">
                    Connect to our global network of AI-powered robots. From precision manufacturing 
                    to personal assistance, experience the future of human-robot collaboration.
                  </p>
                  <div className="flex items-center justify-between">
                    <Button 
                      variant="outline" 
                      size="md" 
                      className="border-electric-blue/50 text-electric-blue hover:bg-electric-blue/10"
                      onClick={() => scrollToSection('technology')}
                    >
                      View Live Fleet
                    </Button>
                    <div className="text-right">
                      <div className="text-sm font-bold text-bright-white">Next Available</div>
                      <div className="text-xs text-electric-blue">2 min 47 sec</div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
            
            {/* Enhanced Floating Tech Elements */}
            <div className="absolute -top-8 -right-8 w-24 h-24 bg-electric-gradient rounded-full blur-xl opacity-40 animate-float-smooth"></div>
            <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-gradient-to-r from-tech-cyan to-accent-electric rounded-full blur-xl opacity-30 animate-float-smooth" style={{animationDelay: '2s'}}></div>
          </motion.div>
        </div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        onClick={() => scrollToSection('technology')}
        whileHover={{ scale: 1.1 }}
      >
        <div className="w-8 h-12 border-2 border-electric-blue/70 rounded-full flex justify-center shadow-interactive">
          <div className="w-2 h-4 bg-electric-blue rounded-full mt-2 animate-pulse-electric"></div>
        </div>
        <p className="text-xs text-subtle-gray mt-3 text-center font-body uppercase tracking-widest">Explore Technology</p>
      </motion.div>
    </div>
  )
}